import React from "react";
import { Button } from "@/components/ui/button";
import { captureError } from "@/lib/error-monitoring";
import {
  clearChunkLoadReloadAttempt,
  shouldReloadForChunkLoadError,
} from "@/lib/chunkLoadRecovery";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureError(error, {
      source: "react.error_boundary",
      componentStack: errorInfo.componentStack,
    });

    if (shouldReloadForChunkLoadError(error)) {
      console.error(JSON.stringify({
        event: "chunk_load_recovery_reload",
        error_message: error.message,
        fix_suggestion: "The app detected a stale lazy-loaded asset and is reloading once to fetch the latest deployment.",
      }));
      window.location.reload();
      return;
    }

    if (import.meta.env.DEV) {
      console.error("Unhandled error:", error);
    }
  }

  handleReload = () => {
    clearChunkLoadReloadAttempt();
    window.location.reload();
  };

  handleReset = () => {
    clearChunkLoadReloadAttempt();
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-bold text-foreground">
              Something went wrong
            </h1>
            <p className="text-sm text-muted-foreground">
              Please refresh the page. If the issue persists, contact support.
            </p>
            {this.state.error && (
              <pre className="text-xs text-left bg-muted/50 p-3 rounded-md overflow-auto max-h-32">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" onClick={this.handleReset}>
                Try Again
              </Button>
              <Button onClick={this.handleReload}>Reload</Button>
            </div>
          </div>
        </div>
      );
    }

    clearChunkLoadReloadAttempt();
    return this.props.children;
  }
}

export default ErrorBoundary;
